// api/dispatchers/panelSubscription.ts — Layer-1 raw subscription side-effects
// for panelDataStore (design §4.2, §4.8).
//
// The panelDataStore is a Zustand store, not a React component, so it cannot
// call useGateway(). CRITICAL SURPRISE #1: there is no `gateway` singleton —
// the live GatewayClient is created in main.tsx and shared only via React
// context. ChartViewport (a component) injects it here via setPanelGateway()
// in an effect, so the store can reach the live client without importing
// context. Until injected, open/close queue the request (see pendingSymbols
// below) instead of dropping it.
//
// openSymbolSubscription / closeSymbolSubscription own the ONE raw
// gateway.subscribe / unsubscribe per symbol (interval OMITTED — the server
// keys subs by channel:symbol, ChannelRegistry.ts:87-88). Unsub closures are
// tracked per symbol so teardown is idempotent under StrictMode double-mount
// (acceptance #6).
//
// ROOT CAUSE (live-chart-never-updates bug, 2026-08-28): openSymbolSubscription
// used to silently no-op when panelGateway was still null and never retried —
// it assumed (see the old comment on setPanelGateway) that "the client is set
// once before any panel mounts". That assumption is false under React 18
// StrictMode: effects fire child-before-parent, so a lazy-loaded panel's own
// subscribe effect (useChartDataSubscription → panelDataStore.subscribe →
// openSymbolSubscription) can and does run before ChartViewport's
// setPanelGateway effect commits, AND StrictMode's synchronous
// mount→unmount→remount churn can toggle the gateway back to null in between
// two attempts. Confirmed live via instrumented timestamps: openSymbolSubscription
// fired at panelGateway=null twice in a row (t=1590ms, t=1678ms) before
// setPanelGateway ever landed a non-null client that stuck (t=1686ms) — by then
// panelDataStore had already bumped the symbol's refcount to 1, so no later event
// re-triggers openSymbolSubscription. The raw ohlc/trades/book WS subscriptions for
// that symbol were never sent, so the chart only ever showed the one-time REST
// snapshot (ensureLoaded) and never ticked live. `pendingSymbols` fixes this: a
// subscribe request that arrives before the gateway is injected is queued, not
// dropped, and flushed the instant a non-null gateway lands.

import type { GatewayClient } from '../gateway';
import type { SubscriptionChannel } from '../types';
import { loadSymbol1m } from '../panelDataFetcher';

// Re-export so consumers importing from the dispatcher (per doc §4.8 budget)
// and from panelDataFetcher both resolve to one implementation.
export { loadSymbol1m };
export type { SymbolLoadResult, SymbolLoadHandle } from '../panelDataFetcher';

/**
 * Channels the per-symbol price feed needs. Phase 2 adds 'book' (NEW-5): the
 * ONE per-symbol refcount owns the book subscription too — there is no separate
 * book refcount. With this, any /chart-next panel that bumps the symbol refcount
 * (useSymbolFeed) gets a live order book in the now per-symbol bookStore, ready
 * for the OrderBook/DepthChart panel wrappers that land in Phase 3.
 */
const PANEL_CHANNELS: readonly SubscriptionChannel[] = ['ohlc', 'trades', 'book'];

let panelGateway: GatewayClient | null = null;

/** Per-symbol unsubscribe closures from the live gateway, for idempotent teardown. */
const openSubs = new Map<string, Array<() => void>>();

/**
 * Symbols that requested a raw subscription while no gateway was injected yet
 * (or while it had been reset to null mid StrictMode-churn). Flushed the instant
 * a non-null gateway is set so a request is queued, never silently dropped.
 */
const pendingSymbols = new Set<string>();

/**
 * Inject the live GatewayClient. Called once from ChartViewport's effect with
 * useGateway(). Idempotent — re-injecting the same client is a no-op; a new
 * client (reconnect / remount) closes any subs opened against the old one.
 * Flushes any symbol that tried to subscribe while the gateway was unset.
 */
export function setPanelGateway(client: GatewayClient | null): void {
  if (panelGateway === client) return;
  // A client swap invalidates old unsub closures; drop them (the old socket is
  // gone) and re-queue those symbols so they re-open against the new client
  // below instead of staying silently torn down.
  if (panelGateway !== null && client !== panelGateway) {
    for (const symbol of openSubs.keys()) pendingSymbols.add(symbol);
    for (const unsubs of openSubs.values()) {
      for (const unsub of unsubs) safeUnsub(unsub);
    }
    openSubs.clear();
  }
  panelGateway = client;
  if (panelGateway !== null && pendingSymbols.size > 0) {
    const toOpen = Array.from(pendingSymbols);
    pendingSymbols.clear();
    for (const symbol of toOpen) openSymbolSubscription(symbol);
  }
}

/** Test/diagnostic hook: how many raw symbol subscriptions are currently open. */
export function openSubscriptionCount(): number {
  return openSubs.size;
}

/**
 * Open the ONE raw subscription set for this symbol (refcount 0→1). Idempotent:
 * a second call while already open is a no-op (guards StrictMode double-invoke).
 * Queues (rather than drops) the request when the gateway has not been injected
 * yet — flushed by setPanelGateway once it lands.
 */
export function openSymbolSubscription(symbol: string): void {
  if (openSubs.has(symbol)) return;
  if (panelGateway === null) {
    pendingSymbols.add(symbol);
    return;
  }
  const unsubs: Array<() => void> = [];
  for (const channel of PANEL_CHANNELS) {
    // interval OMITTED — dropped server-side for ohlc/trades (subscriptionHandler.ts:68).
    unsubs.push(panelGateway.subscribe(channel, symbol));
  }
  openSubs.set(symbol, unsubs);
  // Phase 2: live ohlc/signals for this symbol now flow into panelDataStore via
  // the dual-path dispatchPanelChartMessage (broadcast-by-symbol). REST still
  // seeds the base 1m series via ensureLoaded; this sub keeps it ticking.
}

/**
 * Close the raw subscription set for this symbol (refcount 1→0). Idempotent:
 * a second call after the subs are already gone is a no-op. Also drops the
 * symbol from the pending queue if it was never actually opened (subscribed
 * then unsubscribed again before the gateway ever landed).
 */
export function closeSymbolSubscription(symbol: string): void {
  pendingSymbols.delete(symbol);
  const unsubs = openSubs.get(symbol);
  if (unsubs === undefined) return;
  openSubs.delete(symbol);
  for (const unsub of unsubs) safeUnsub(unsub);
}

function safeUnsub(unsub: () => void): void {
  try {
    unsub();
  } catch {
    // The socket may already be torn down; unsubscribe is best-effort.
  }
}
